import { Collapse, TextField } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PromptAssistInput } from '../api/promptAssist.api';

import { PromptAssistButton } from './PromptAssistButton';

interface PromptInputProps {
  prompt: string;
  negativePrompt: string;
  maxChars: number;
  required: boolean;
  assistContext: Omit<PromptAssistInput, 'prompt'>;
  onPromptChange: (value: string) => void;
  onNegativePromptChange: (value: string) => void;
}

export const PromptInput: React.FC<PromptInputProps> = ({
  prompt,
  negativePrompt,
  maxChars,
  required,
  assistContext,
  onPromptChange,
  onNegativePromptChange,
}) => {
  const { t } = useTranslation();
  const [advancedOpen, setAdvancedOpen] = useState(Boolean(negativePrompt));

  return (
    <div className="space-y-2">
      <TextField
        fullWidth
        multiline
        minRows={4}
        maxRows={10}
        required={required}
        label={t(
          required
            ? 'services.generateVideo.promptLabel'
            : 'services.generateVideo.promptOptionalLabel'
        )}
        placeholder={t('services.generateVideo.promptPlaceholder')}
        value={prompt}
        onChange={(event) => onPromptChange(event.target.value.slice(0, maxChars))}
        helperText={`${prompt.length}/${maxChars}`}
        FormHelperTextProps={{ className: 'text-right' }}
      />
      <PromptAssistButton context={assistContext} prompt={prompt} onChange={onPromptChange} />
      <button
        type="button"
        onClick={() => setAdvancedOpen((open) => !open)}
        className="flex items-center gap-1 text-sm text-blue-600 hover:underline dark:text-blue-400"
        aria-expanded={advancedOpen}
      >
        <ChevronDown size={16} className={advancedOpen ? 'rotate-180 transition' : 'transition'} />
        {t('services.generateVideo.advanced')}
      </button>
      <Collapse in={advancedOpen}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          label={t('services.generateVideo.negativePromptLabel')}
          placeholder={t('services.generateVideo.negativePromptPlaceholder')}
          value={negativePrompt}
          onChange={(event) => onNegativePromptChange(event.target.value.slice(0, maxChars))}
        />
      </Collapse>
    </div>
  );
};
